import { Injectable } from '@angular/core';
import { AuthenticationService } from './authentication.service';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissionsSubject = new BehaviorSubject<string[]>([]);

  constructor(private authService: AuthenticationService) {
    // Suscribirse a cambios en el usuario logueado
    this.authService.loggedUser$.subscribe(user => {
      if (user && user.roles && user.roles.length > 0) {
        const permissions = user.roles
          .filter(role => !!role?.permissions && role.permissions.length > 0)
          .flatMap(role => role.permissions!)
          .map(permission => permission.key!)
          .filter((key, index, self) => !!key && self.indexOf(key) === index);
        this.permissionsSubject.next(permissions);
      } else {
        this.permissionsSubject.next([]);
      }
    });
  }

  /**
   * Verifica si el usuario tiene un permiso específico
   */
  hasPermission(permissionKey: string): boolean {
    const permissions = this.permissionsSubject.value;
    return permissions.includes(permissionKey);
  }

  /**
   * Verifica si el usuario tiene al menos uno de los permisos especificados
   */
  hasAnyPermission(permissionKeys: string[]): boolean {
    const permissions = this.permissionsSubject.value;
    return permissionKeys.some(key => permissions.includes(key));
  }

  /**
   * Verifica si el usuario tiene todos los permisos especificados
   */
  hasAllPermissions(permissionKeys: string[]): boolean {
    const permissions = this.permissionsSubject.value;
    return permissionKeys.every(key => permissions.includes(key));
  }

  /**
   * Obtiene la lista de permisos del usuario
   */
  getPermissions(): Observable<string[]> {
    return this.permissionsSubject.asObservable();
  }

  /**
   * Verifica permisos específicos para Dashboard
   */
  canAccessDashboard(): boolean {
    return this.hasPermission('CanReadDashboard');
  }

  /**
   * Verifica permisos para ver favoritos
   */
  canReadFavorite(): boolean {
    return this.hasPermission('CanReadFavorite');
  }

  /**
   * Verifica permisos específicos para Usuarios
   */
  canReadUsers(): boolean {
    return this.hasPermission('CanReadUsers');
  }

  canCreateUsers(): boolean {
    return this.hasPermission('CanCreateUsers');
  }

  canUpdateUsers(): boolean {
    return this.hasPermission('CanUpdateUsers');
  }

  canDeleteUsers(): boolean {
    return this.hasPermission('CanDeleteUsers');
  }

  canAccessUsers(): boolean {
    return this.hasAnyPermission(['CanReadUsers', 'CanCreateUsers', 'CanUpdateUsers', 'CanDeleteUsers']);
  }

  /**
   * Verifica permisos específicos para Roles
   */
  canReadRoles(): boolean {
    return this.hasPermission('CanReadRoles');
  }

  canCreateRoles(): boolean {
    return this.hasPermission('CanCreateRoles');
  }

  canUpdateRoles(): boolean {
    return this.hasPermission('CanUpdateRoles');
  }

  canDeleteRoles(): boolean {
    return this.hasPermission('CanDeleteRoles');
  }

  canAccessRoles(): boolean {
    return this.hasAnyPermission(['CanReadRoles', 'CanCreateRoles', 'CanUpdateRoles', 'CanDeleteRoles']);
  }

  /**
   * Verifica permisos específicos para Integraciones
   */
  canReadIntegrations(): boolean {
    return this.hasPermission('CanReadIntegrations');
  }

  canCreateIntegrations(): boolean {
    return this.hasPermission('CanCreateIntegrations');
  }

  canUpdateIntegrations(): boolean {
    return this.hasPermission('CanUpdateIntegrations');
  }

  canDeleteIntegrations(): boolean {
    return this.hasPermission('CanDeleteIntegrations');
  }

  canAccessIntegrations(): boolean {
    return this.hasAnyPermission(['CanReadIntegrations', 'CanCreateIntegrations', 'CanUpdateIntegrations', 'CanDeleteIntegrations']);
  }

  /**
   * Verifica si el usuario tiene rol de administrador
   */
  isAdmin(): boolean {
    const user = this.authService.loggedUser$.value;
    return !!(user && user.roles && user.roles.some(role => !!role?.isAdmin));
  }
}
